// src/components/ui/PricingCard.jsx
import React from 'react';
import { Card, Button, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const PricingCard = ({
    planName,
    price,
    originalPrice, // NUEVO: para mostrar el precio original tachado
    priceFrequency = "/ month",
    description,
    features,
    buttonText = "Get Started",
    buttonVariant = "primary",
    buttonLink = "/signup",
    isPopular = false,
    promoNotice, // NUEVO: para un mensaje promocional como "Launch Offer"
    cardClassName = "shadow-lg h-100",
    headerClassName = "bg-light border-bottom-0 py-3",
    bodyClassName = "d-flex flex-column p-4",
    ...restCardProps
}) => {
    return (
        <Card className={`pricing-card ${cardClassName} ${isPopular ? 'border-primary popular-plan' : ''}`} {...restCardProps}>
            {/* Badge de "POPULAR" (existente) */}
            {isPopular && !promoNotice && ( // Solo muestra "POPULAR" si no hay un promoNotice más específico
                <div
                    className="popular-badge bg-primary text-white px-3 py-1 position-absolute rounded-pill shadow-sm"
                    style={{
                        top: '15px',
                        right: '15px',
                        transform: 'rotate(10deg)',
                        zIndex: 10,
                        fontSize: '0.8rem',
                        lineHeight: '1.2',
                    }}
                >
                    POPULAR
                </div>
            )}
            {/* Badge de Promoción (NUEVO) */}
            {promoNotice && (
                <div
                    className={`promo-notice-badge ${isPopular ? 'bg-danger' : 'bg-success'} text-white px-3 py-1 position-absolute rounded-pill shadow-sm fw-bold`}
                    style={{
                        top: '15px', // Ajusta si es necesario para que no colisione con "POPULAR" o estilízalo diferente
                        left: '50%',
                        transform: 'translateX(-50%)', // Centrado
                        // O si quieres que esté en una esquina como el de POPULAR pero diferente:
                        // right: '15px',
                        // transform: 'rotate(10deg)', // Si quieres la misma rotación
                        zIndex: 11, // Por encima del de popular si ambos estuvieran (aunque la lógica de arriba lo evita)
                        fontSize: '0.85rem',
                        lineHeight: '1.3',
                    }}
                >
                    {promoNotice}
                </div>
            )}

            <Card.Header className={`${headerClassName} text-center position-relative pt-4`}> {/* Añadido pt-4 si el badge está arriba */}
                <h3 className="fw-bold mb-0">{planName}</h3>
            </Card.Header>

            <Card.Body className={`${bodyClassName} text-center`}>
                <div className="mb-4">
                    {originalPrice && price !== originalPrice && ( // Muestra precio original si es diferente al actual
                        <span className="display-6 fw-normal text-muted text-decoration-line-through me-2">${originalPrice}</span>
                    )}
                    <span className="display-4 fw-bolder">${price}</span>
                    <span className="text-muted">{priceFrequency}</span>
                </div>
                <p className="text-muted mb-4">{description}</p>

                {features && features.length > 0 && (
                    <ListGroup variant="flush" className="text-start mb-4 flex-grow-1">
                        {features.map((feature, index) => (
                            <ListGroup.Item key={index} className="px-0 border-0 bg-transparent d-flex align-items-center">
                                {typeof feature === 'string' ? (
                                    <>
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 flex-shrink-0" />
                                        <span>{feature}</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon
                                            icon={feature.included ? faCheckCircle : faTimesCircle}
                                            className={feature.included ? 'text-success me-2 flex-shrink-0' : 'text-danger me-2 flex-shrink-0'}
                                        />
                                        <span className={!feature.included ? 'text-decoration-line-through text-muted' : ''}>
                                            {feature.text}
                                        </span>
                                    </>
                                )}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}

                <Button
                    as={Link}
                    to={`${buttonLink}${buttonLink.includes('?') ? '&' : '?'}plan=${planName.toLowerCase().replace(/\s+/g, '-')}`}
                    variant={buttonVariant}
                    size="lg"
                    className="w-100 mt-auto fw-bold"
                >
                    {buttonText}
                </Button>
            </Card.Body>
        </Card>
    );
};

PricingCard.propTypes = {
    planName: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    originalPrice: PropTypes.string, // Nuevo
    priceFrequency: PropTypes.string,
    description: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                text: PropTypes.string.isRequired,
                included: PropTypes.bool.isRequired,
                isTitle: PropTypes.bool, // Para las características que son títulos
            })
        ])
    ).isRequired,
    buttonText: PropTypes.string,
    buttonVariant: PropTypes.string,
    buttonLink: PropTypes.string,
    isPopular: PropTypes.bool,
    promoNotice: PropTypes.string, // Nuevo
    cardClassName: PropTypes.string,
    headerClassName: PropTypes.string,
    bodyClassName: PropTypes.string,
};

export default PricingCard;